/**
 * @file footer-legal.ts
 * @description Нижняя полоса подвала — юридическая строка и ссылки платформы
 * (phase19.16, приёмка Р2а, решение пользователя 2026-10-11): настройки вида
 * выше нормы SB19 (SB118), порядок ссылок платформы, копирайт «© годы
 * правообладатель» (SB114), подписи вариантов для кабинета.
 *
 * Часть документа оформления `theme.footer.legal` (schema.ts берёт отсюда
 * схему и умолчание). Состав сведений о продавце здесь не настраивается —
 * он под замком платформы (SB19, ПП № 657 п. 22); настраивается только вид
 * и то, что закон разрешает выбирать: телефон и/или email (хотя бы одно).
 *
 * Умолчания = вид до SB118 (одна строка через «·», слева, норма 13 px, цвет
 * текста подвала, линия-разделитель, порядок «оферта, политика, возврат,
 * доставка и оплата, кабинет», телефон и email показаны, копирайт сверху
 * полосы, все ссылки включены) — старые темы
 * читаются через `mergeMissingKeys` без смены версии документа.
 */

import { z } from "zod";

/** Ссылки платформы в нижней полосе (SB17, SB19, SB86, SB115). */
export const FOOTER_PLATFORM_LINKS = ["offer", "privacy", "returns", "delivery", "account"] as const;
export type FooterPlatformLink = (typeof FOOTER_PLATFORM_LINKS)[number];

/**
 * Ссылки, которые прячутся, если пункт меню подвала ведёт туда же (SB115).
 * Оферта и политика ПДн — всегда в полосе (152-ФЗ ст. 18.1, SB17).
 */
export const FOOTER_DEDUPLICATED_LINKS = ["delivery", "returns", "account"] as const;
export type FooterDeduplicatedLink = (typeof FOOTER_DEDUPLICATED_LINKS)[number];

/**
 * SB118 (дополнение 2026-10-11): ссылки, которые владелец выключает по одной
 * (`hiddenLinks`). Оферта и политика ПДн не выключаются — FOOTER_LOCKED_LINKS.
 */
export const FOOTER_HIDEABLE_LINKS = ["returns", "delivery", "account"] as const;
export type FooterHideableLink = (typeof FOOTER_HIDEABLE_LINKS)[number];

/** Ссылки под замком: всегда в полосе (152-ФЗ ст. 18.1, SB17). */
export const FOOTER_LOCKED_LINKS = ["offer", "privacy"] as const;
export type FooterLockedLink = (typeof FOOTER_LOCKED_LINKS)[number];

/** Пояснение к замку у оферты и политики в кабинете. */
export const FOOTER_LOCKED_LINK_NOTE = "По закону на каждой странице";

/** Адрес кабинета покупателя на витрине (ссылка платформы «Личный кабинет»). */
export const FOOTER_ACCOUNT_HREF = "/lk";

export const FOOTER_LEGAL_LAYOUTS = ["inline", "lines", "columns"] as const;
export const FOOTER_LEGAL_ALIGNS = ["left", "center"] as const;
export const FOOTER_LEGAL_SIZES = ["normal", "large"] as const;
export const FOOTER_LEGAL_TONES = ["text", "muted"] as const;
export const FOOTER_LEGAL_DIVIDERS = ["line", "gap"] as const;
/** SB114 (дополнение 2026-10-11): где стоит копирайт. */
export const FOOTER_COPYRIGHT_PLACES = ["top", "bottom", "about"] as const;

export type FooterLegalLayout = (typeof FOOTER_LEGAL_LAYOUTS)[number];
export type FooterLegalAlign = (typeof FOOTER_LEGAL_ALIGNS)[number];
export type FooterLegalSize = (typeof FOOTER_LEGAL_SIZES)[number];
export type FooterLegalTone = (typeof FOOTER_LEGAL_TONES)[number];
export type FooterLegalDivider = (typeof FOOTER_LEGAL_DIVIDERS)[number];
export type FooterCopyrightPlace = (typeof FOOTER_COPYRIGHT_PLACES)[number];

/**
 * Настройки нижней полосы (SB118). `linksOrder` — порядок показа; ключи,
 * которых в списке нет (старый документ, новая ссылка платформы), идут
 * следом в порядке FOOTER_PLATFORM_LINKS — поэтому полный список не
 * обязателен и добавление ссылки платформы не ломает сохранённые темы.
 */
export const siteFooterLegalSchema = z
  .strictObject({
    layout: z.enum(FOOTER_LEGAL_LAYOUTS),
    align: z.enum(FOOTER_LEGAL_ALIGNS),
    /** Меньше нормы платформы (13 px) нельзя — вариантов «мельче» нет (SB19). */
    size: z.enum(FOOTER_LEGAL_SIZES),
    /** «Приглушённый» витрина применяет только при контрасте ≥ 4.5:1 к фону подвала. */
    tone: z.enum(FOOTER_LEGAL_TONES),
    divider: z.enum(FOOTER_LEGAL_DIVIDERS),
    linksOrder: z
      .array(z.enum(FOOTER_PLATFORM_LINKS))
      .max(FOOTER_PLATFORM_LINKS.length)
      .refine((list) => new Set(list).size === list.length, "Ссылка в порядке указана дважды"),
    showPhone: z.boolean(),
    showEmail: z.boolean(),
    /**
     * SB114 (решение 2026-10-11): копирайт первой строкой полосы, последней
     * строкой полосы или в колонке «О магазине». Сам текст — `footer.copyright`
     * ответа витрины; показывать ли его — `footer.show.copyright`.
     */
    copyrightPlace: z.enum(FOOTER_COPYRIGHT_PLACES),
    /**
     * SB118 (дополнение 2026-10-11): выключенные ссылки платформы. Действует
     * поверх скрытия дублей (SB115); оферты и политики в списке быть не может.
     */
    hiddenLinks: z
      .array(z.enum(FOOTER_HIDEABLE_LINKS))
      .max(FOOTER_HIDEABLE_LINKS.length)
      .refine((list) => new Set(list).size === list.length, "Ссылка в списке скрытых указана дважды"),
  })
  // ПП № 657 п. 22: способ связи с продавцом — email и/или телефон, хотя бы один.
  .refine((v) => v.showPhone || v.showEmail, {
    message: "Покажите хотя бы одно: телефон или email продавца",
    path: ["showPhone"],
  });

export type SiteFooterLegal = z.infer<typeof siteFooterLegalSchema>;

/** Умолчание = вид нижней полосы до SB118. Не замораживается здесь — его замораживает DEFAULT_SITE_THEME. */
export function defaultSiteFooterLegal(): SiteFooterLegal {
  return {
    layout: "inline",
    align: "left",
    size: "normal",
    tone: "text",
    divider: "line",
    linksOrder: [...FOOTER_PLATFORM_LINKS],
    showPhone: true,
    showEmail: true,
    copyrightPlace: "top",
    hiddenLinks: [],
  };
}

/**
 * Порядок ссылок для показа: сначала из `linksOrder`, затем недостающие в
 * порядке платформы. `present` — какие ссылки вообще есть (опубликован ли
 * документ, не скрыта ли как дубль); отсутствующие пропускаются.
 */
export function orderFooterPlatformLinks(
  linksOrder: readonly FooterPlatformLink[],
  present: ReadonlySet<FooterPlatformLink> = new Set(FOOTER_PLATFORM_LINKS),
): FooterPlatformLink[] {
  const seen = new Set<FooterPlatformLink>();
  const out: FooterPlatformLink[] = [];
  for (const kind of [...linksOrder, ...FOOTER_PLATFORM_LINKS]) {
    if (seen.has(kind)) continue;
    seen.add(kind);
    if (present.has(kind)) out.push(kind);
  }
  return out;
}

// ── Подписи для кабинета ──

export const FOOTER_LEGAL_LAYOUT_OPTIONS: Readonly<Record<FooterLegalLayout, string>> = {
  inline: "В одну строку",
  lines: "Построчно",
  columns: "Две колонки на ПК",
};

export const FOOTER_LEGAL_ALIGN_OPTIONS: Readonly<Record<FooterLegalAlign, string>> = {
  left: "Слева",
  center: "По центру",
};

export const FOOTER_LEGAL_SIZE_OPTIONS: Readonly<Record<FooterLegalSize, string>> = {
  normal: "Обычный",
  large: "Крупнее",
};

export const FOOTER_LEGAL_TONE_OPTIONS: Readonly<Record<FooterLegalTone, string>> = {
  text: "Как текст подвала",
  muted: "Приглушённый",
};

export const FOOTER_LEGAL_DIVIDER_OPTIONS: Readonly<Record<FooterLegalDivider, string>> = {
  line: "Линия",
  gap: "Отступ без линии",
};

export const FOOTER_COPYRIGHT_PLACE_OPTIONS: Readonly<Record<FooterCopyrightPlace, string>> = {
  top: "Сверху полосы",
  bottom: "Снизу полосы",
  about: "В колонке «О магазине»",
};

/** Подписи ссылок платформы (тексты ссылок на сайте — у сервера, LEGAL_LABELS). */
export const FOOTER_PLATFORM_LINK_LABELS: Readonly<Record<FooterPlatformLink, string>> = {
  offer: "Публичная оферта",
  privacy: "Политика обработки персональных данных",
  returns: "Условия возврата",
  delivery: "Условия доставки и оплаты",
  account: "Личный кабинет",
};

/** Подписи полей настройки — для кабинета и строки «что изменено». */
export const FOOTER_LEGAL_FIELD_LABELS: Readonly<Record<keyof SiteFooterLegal, string>> = {
  layout: "Нижняя полоса: раскладка",
  align: "Нижняя полоса: выравнивание",
  size: "Нижняя полоса: размер",
  tone: "Нижняя полоса: тон",
  divider: "Нижняя полоса: разделитель",
  linksOrder: "Нижняя полоса: порядок ссылок",
  showPhone: "Нижняя полоса: телефон",
  showEmail: "Нижняя полоса: email",
  copyrightPlace: "Копирайт: место",
  hiddenLinks: "Скрытые ссылки",
};

/** Подпись значения поля нижней полосы для людей; null — значение неизвестно. */
export function footerLegalValueLabel(field: keyof SiteFooterLegal, value: unknown): string | null {
  const pick = (map: Readonly<Record<string, string>>): string | null =>
    typeof value === "string" ? (map[value] ?? null) : null;
  switch (field) {
    case "layout":
      return pick(FOOTER_LEGAL_LAYOUT_OPTIONS);
    case "align":
      return pick(FOOTER_LEGAL_ALIGN_OPTIONS);
    case "size":
      return pick(FOOTER_LEGAL_SIZE_OPTIONS);
    case "tone":
      return pick(FOOTER_LEGAL_TONE_OPTIONS);
    case "divider":
      return pick(FOOTER_LEGAL_DIVIDER_OPTIONS);
    case "linksOrder":
    case "hiddenLinks":
      // Пустой список скрытых — «нет», а не пустая строка.
      if (field === "hiddenLinks" && Array.isArray(value) && value.length === 0) return "нет";
      return Array.isArray(value)
        ? value
            .map((k) => (typeof k === "string" ? (FOOTER_PLATFORM_LINK_LABELS as Record<string, string>)[k] : undefined))
            .filter((label): label is string => label !== undefined)
            .join(", ")
        : null;
    case "copyrightPlace":
      return pick(FOOTER_COPYRIGHT_PLACE_OPTIONS);
    case "showPhone":
    case "showEmail":
      return typeof value === "boolean" ? (value ? "показан" : "скрыт") : null;
  }
}

// ── Копирайт (SB114) ──

/** Шаблон копирайта по умолчанию: «© год правообладатель» (ГК РФ ст. 1271). */
export const FOOTER_COPYRIGHT_DEFAULT_TEMPLATE = "© {year} {legalName}";

/**
 * Копирайт нижней полосы. Шаблон владельца (`brand.copyrightText`) с
 * подстановками: `{year}` — текущий год, `{legalName}` — наименование
 * продавца из реквизитов (нет реквизитов — название сайта), `{siteName}` —
 * название сайта. Годы «с … по текущий» владелец пишет сам: «© 2024–{year}
 * {legalName}» — отдельного поля «год начала» в настройках нет (оно было бы
 * колонкой `SiteDraft`, а шаблон уже это умеет). null — шаблон по умолчанию.
 * Год — параметром (тестируемость).
 */
export function formatFooterCopyright(
  template: string | null,
  values: { siteName: string; legalName: string | null; year: number },
): string {
  const source = template?.trim() ? template : FOOTER_COPYRIGHT_DEFAULT_TEMPLATE;
  const holder = values.legalName?.trim() || values.siteName;
  return source
    .replaceAll("{year}", String(values.year))
    .replaceAll("{legalName}", holder)
    .replaceAll("{siteName}", values.siteName);
}
